import { useEffect, useState } from 'react'
import { AbiItem } from 'web3-utils'
import { ContractOptions } from 'web3-eth-contract'
import useWeb3 from 'hooks/useWeb3'
import { 
  getMasterChefAddress, getCakeAddress, getLotteryAddress, getLotteryTicketAddress, getTempleAddress,
  getPresellerAddress, getIboFactoryAddress, getBenSwapLpValidatorAddress, getBenLockFactoryAddress,
  getSep20FactoryAddress, getSep20TemplateBasicAddress, getNFTEXAddress, getFogHeroAddress, getFogItemAddress, getPokeBenCrystalAddress, getCemeteryControllerAddress, getCemeteryNftAddress
} from 'utils/addressHelpers'
import { poolsConfig } from 'config/constants'
import { PoolCategory } from 'config/constants/types'
import ifo from 'config/abi/ifo.json'
import erc20 from 'config/abi/erc20.json'
import rabbitmintingfarm from 'config/abi/rabbitmintingfarm.json'
import pancakeRabbits from 'config/abi/pancakeRabbits.json'
import lottery from 'config/abi/lottery.json'
import lotteryTicket from 'config/abi/lotteryNft.json'
import temple from 'config/abi/temple.json'
import masterChef from 'config/abi/masterchef.json'
import sousChef from 'config/abi/sousChef.json'
import sousChefBnb from 'config/abi/sousChefBnb.json'
import preseller from 'config/abi/preseller.json'
import iboFactory from 'config/abi/iboFactory.json'
import ibo from 'config/abi/ibo.json'
import iSingleAddressValidator from 'config/abi/iSingleAddressValidator.json'
import benLock from 'config/abi/benLock.json'
import benLockFactory from 'config/abi/benLockFactory.json'
import iSep20Template from 'config/abi/iSep20Template.json'
import sep20Factory from 'config/abi/sep20Factory.json'
import sep20FactoryTokenBasic from 'config/abi/sep20FactoryTokenBasic.json'
import sep20FactoryTokenLiquidityGenerator from 'config/abi/sep20FactoryTokenLiquidityGenerator.json'
import sep20FactoryTokenAnnuity from 'config/abi/sep20FactoryTokenAnnuity.json'
import sep20FactoryTokenMultiMint from 'config/abi/sep20FactoryTokenMultiMint.json'
import sep20FactoryTokenInscriptions from 'config/abi/sep20FactoryTokenInscriptions.json'
import ENS_ABI from 'config/abi/ens-registrar.json'
import ENS_PUBLIC_RESOLVER_ABI from 'config/abi/ens-public-resolver.json'
import NFTEX from 'config/abi/NFTEX.json'
import erc721 from 'config/abi/erc721.json'
import abiFogHero from 'config/abi/fogHero.json'
import abiFogItem from 'config/abi/fogItem.json'
import abiCemetery from 'config/abi/cemetery.json'

export const useContract = (abi: AbiItem, address: string, contractOptions?: ContractOptions) => {
  const web3 = useWeb3()
  const [contract, setContract] = useState(new web3.eth.Contract(abi, address, contractOptions))

  useEffect(() => {
    setContract(new web3.eth.Contract(abi, address, contractOptions))
  }, [abi, address, contractOptions, web3])

  return contract
}

/**
 * Helper hooks to get specific contracts (by ABI)
 */

export const useIfoContract = (address: string) => {
  const ifoAbi = (ifo as unknown) as AbiItem
  return useContract(ifoAbi, address)
}

export const useERC20 = (address: string) => {
  const erc20Abi = (erc20 as unknown) as AbiItem
  return useContract(erc20Abi, address)
}

export const useERC721 = (address: string) => {
  const er721Abi = (erc721 as unknown) as AbiItem
  return useContract(er721Abi, address)
}

export const useCake = () => {
  return useERC20(getCakeAddress())
}

export const useRabbitMintingFarm = (address: string) => {
  const rabbitMintingFarmAbi = (rabbitmintingfarm as unknown) as AbiItem
  return useContract(rabbitMintingFarmAbi, address)
}

export const usePancakeRabbits = (address: string) => {
  const pancakeRabbitsAbi = (pancakeRabbits as unknown) as AbiItem
  return useContract(pancakeRabbitsAbi, address)
}

export const useLottery = () => {
  const abi = (lottery as unknown) as AbiItem
  return useContract(abi, getLotteryAddress())
}

export const useLotteryTicket = () => {
  const abi = (lotteryTicket as unknown) as AbiItem
  return useContract(abi, getLotteryTicketAddress())
}

export const useTemple = () => {
  const abi = (temple as unknown) as AbiItem
  return useContract(abi, getTempleAddress())
}

export const useMasterchef = () => {
  const abi = (masterChef as unknown) as AbiItem
  return useContract(abi, getMasterChefAddress())
}

export const useSousChef = (id) => {
  const config = poolsConfig.find((pool) => pool.sousId === id)
  const rawAbi = config.poolCategory === PoolCategory.BINANCE ? sousChefBnb : sousChef
  const abi = (rawAbi as unknown) as AbiItem
  return useContract(abi, config.contractAddress[process.env.REACT_APP_CHAIN_ID])
}

export const usePreseller = () => {
  const abi = (preseller as unknown) as AbiItem
  return useContract(abi, getPresellerAddress())
}

export const useIboFactory = () => {
  const abi = (iboFactory as unknown) as AbiItem
  return useContract(abi, getIboFactoryAddress())
}

export const useIbo = (address: string) => {
  const abi = (ibo as unknown) as AbiItem
  return useContract(abi, address)
}

export const useBenSwapLpValidator = () => {
  const abi = (iSingleAddressValidator as unknown) as AbiItem
  return useContract(abi, getBenSwapLpValidatorAddress())
}

export const useBenLockFactory = () => {
  const abi = (benLockFactory as unknown) as AbiItem
  return useContract(abi, getBenLockFactoryAddress())
}

export const useBenLock = (address: string) => {
  const abi = (benLock as unknown) as AbiItem
  return useContract(abi, address)
}

export const useSep20Factory = () => {
  const abi = (sep20Factory as unknown) as AbiItem
  return useContract(abi, getSep20FactoryAddress())
}

export const useSep20TemplateBasic = () => {
  const abi = (iSep20Template as unknown) as AbiItem
  return useContract(abi, getSep20TemplateBasicAddress())
}

export const useSep20FactoryTokenBasic = (address: string) => {
  const abi = (sep20FactoryTokenBasic as unknown) as AbiItem
  return useContract(abi, address)
}

export const useSep20FactoryTokenLiquidityGenerator = (address: string) => {
  const abi = (sep20FactoryTokenLiquidityGenerator as unknown) as AbiItem
  return useContract(abi, address)
}

export const useSep20FactoryTokenAnnuity = (address: string) => {
  const abi = (sep20FactoryTokenAnnuity as unknown) as AbiItem
  return useContract(abi, address)
}

export const useSep20FactoryTokenMultiMint = (address: string) => {
  const abi = (sep20FactoryTokenMultiMint as unknown) as AbiItem
  return useContract(abi, address)
}

export const useSep20FactoryTokenInscriptions = (address: string) => {
  const abi = (sep20FactoryTokenInscriptions as unknown) as AbiItem
  return useContract(abi, address)
}

export function useENSRegistrarContract(withSignerIfPossible?: boolean) {
  const ENS_REGISTRAR_ADDRESS = {
    10000: '0xCfb86556760d03942EBf1ba88a9870e67D77b627',
    10001: '0x32f1FBE59D771bdB7FB247FE97A635f50659202b',
  }

  const chainId = process.env.REACT_APP_CHAIN_ID;
  const address = ENS_REGISTRAR_ADDRESS[chainId];
  return useContract(ENS_ABI as any, address)
}

export function useENSResolverContract(address: string | undefined, withSignerIfPossible?: boolean) {
  return useContract(ENS_PUBLIC_RESOLVER_ABI as any, address)
}

export const useNFTEX = () => {
  const abi = (NFTEX as unknown) as AbiItem
  return useContract(abi, getNFTEXAddress())
}

export const useFogHero = () => {
  const abi = (abiFogHero as unknown) as AbiItem
  return useContract(abi, getFogHeroAddress())
}

export const useFogItem = () => {
  const abi = (abiFogItem as unknown) as AbiItem
  return useContract(abi, getFogItemAddress())
}

export const usePokeBenCrystal = () => {
  return useSep20FactoryTokenMultiMint(getPokeBenCrystalAddress())
}

export const useCemeteryController = () => {
  const abi = (abiCemetery as unknown) as AbiItem
  return useContract(abi,getCemeteryControllerAddress())
}

export const useCemeteryNft = () => {
  const abi = (erc721 as unknown) as AbiItem
  return useContract(abi, getCemeteryNftAddress())
}

export default useContract
